import React, { useEffect, useRef, useState } from 'react'
import { App, Modal, Form, Checkbox, Select, DatePicker, Input, Upload, TreeSelect, InputRef, Tag } from 'antd'
import { CloseCircleOutlined, InfoCircleOutlined, LoadingOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import ImgCrop from 'antd-img-crop'
import dayjs from 'dayjs'

import './styles.less'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { isMicrosoftPayment } from '../../store/auth-company-slice'
import { validEmailWithRegex } from '@vacationtracker/shared/functions/valid-email-with-regex'
import { resolveNameParts, joinFullName } from '@vacationtracker/shared/functions/split-full-name'
import useDebounce from '../../util/use-debounce'

import { uploadAvatar } from '../../services/api/files'
import IntlMessages from '../../util/IntlMessages'

import { IUserProps } from '../user-profile-tab/types'
import { IGetTeamsShort } from '../../types/teams'
import { IGetLocationsShort } from '../../types/locations'
import { RcFile } from 'antd/lib/upload/interface'
import { UserStatus } from '@vacationtracker/shared/types/user'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { DefaultRoleIdEnum, IRoleAssignment } from '@vacationtracker/shared/types/roles'
import { useLocationAdmin } from '../../hooks/use-location-admin'
import { filterOptions } from '../../util/filter-options'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'

const { Option } = Select

interface IUserUpdateFormProps {
  initialRolloverChanged: boolean
  user: IUserProps
  roleAssignments?: IRoleAssignment[]
  onSave: (v) => void
  handleCancel: () => void
  visible: boolean
  teams: IGetTeamsShort[]
  locations: IGetLocationsShort[]
  isLocationAdmin?: boolean
  enableUserEndDate: boolean | undefined
  companyPlan: SubscriptionPlanEnum
}

interface IUserData {
  changeTeam: boolean
  changeLocation: boolean
  changeStatus: boolean
  changeName: boolean
  changeEmail?: boolean
  name: string
  firstName: string
  lastName: string
  status: UserStatus
  teamId: string
  locationId: string
  email?: string
  isAdmin: boolean
  role: string
  managedLocationIds?: string[]
  startDate: string
  endDate?: string
  employeeId?: string
  imageUrl?: string
  birthday?: string
}

const UserUpdateForm = ({
  initialRolloverChanged,
  user,
  roleAssignments,
  onSave,
  handleCancel,
  visible,
  teams,
  locations,
  isLocationAdmin,
  enableUserEndDate = false,
  companyPlan,
}: IUserUpdateFormProps) => {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const isMicrosoftBillingPayment = useSelector(isMicrosoftPayment)
  const { isLocationAdmin: amILocationAdmin, managedLocationIds } = useLocationAdmin()
  const showLocationAdminOption = companyPlan !== SubscriptionPlanEnum.freemium
  const hasNestedTeams = teams.some((team) => team.parentId)
  const loggedInAdminId = authUser.id
  // A location admin may edit an Admin user's other fields but not their role, email or status.
  const cannotEditAdminFields = Boolean(isLocationAdmin && user.isAdmin)
  const [emailValue, setEmailValue] = useState(user.email || '')
  const [isEmailValid, setIsEmailValid] = useState(true)
  const debouncedEmail = useDebounce(emailValue, 300) // 300ms debounce
  const emailInputRef = useRef<InputRef | null>(null)
  const locationAdminAssignment = roleAssignments?.find((a) => a.roleId === DefaultRoleIdEnum.LOCATION_ADMIN)
  const currentRole = locationAdminAssignment ? DefaultRoleIdEnum.LOCATION_ADMIN : user.role
  const defaultManagedLocationIds = locationAdminAssignment?.scope?.resourceIds ?? []

  const defaultValue = {
    isActive: user.status === 'ACTIVE' ? true : false,
    role: currentRole,
    ...resolveNameParts(user.firstName, user.lastName, user.name),
    isAdmin: user.isAdmin,
    email: user.email,
    teamId: user.team.id,
    locationId: user.location.id,
    startDate: dayjs(user.startDate.split('T')[0]),
    endDate: user.endDate ? dayjs(user.endDate.split('T')[0]) : '',
    employeeId: user.employeeId ?? '',
    birthday: user?.birthday ? dayjs(user.birthday, 'YYYY-MM-DD') : '',
    managedLocationIds: defaultManagedLocationIds,
  }

  const [selectedRole, setSelectedRole] = useState(currentRole)

  const [showWarningForInitialRolloverLocationChanged, setShowWarningForInitialRolloverLocationChanged] = useState(false)
  const [showWarningForInitialRolloverStartDateChanged, setShowWarningForInitialRolloverStartDateChanged] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [uploadInProgress, setUploadInProgress] = useState(false)
  const [selectedFile, setSelectedFile] = useState<RcFile | null>(null)
  const [disableStatus, setDisableStatus] = useState(() => {
    return user.isAdmin && user.status === 'ACTIVE'
  })

  useEffect(() => {
    emailInputRef.current?.focus()
  }, [])

  // Effect to validate email after debounce
  useEffect(() => {
    if (debouncedEmail) {
      if (debouncedEmail === user.email) {
        setIsEmailValid(true)
        return
      }

      const valid = validEmailWithRegex(debouncedEmail)
      setIsEmailValid(valid)
    }
  }, [debouncedEmail, user.email])

  const onChangeUserRole = (newRole) => {
    setSelectedRole(newRole)
    if (newRole === DefaultRoleIdEnum.LOCATION_ADMIN && defaultManagedLocationIds.length === 0) {
      form.setFieldsValue({ managedLocationIds: [user.location.id] })
    }
    if (newRole === 'Admin') {
      setDisableStatus(true)
      form.setFieldsValue({ isActive: true })
    } else {
      setDisableStatus(false)
    }
  }

  const onChangeLocation = (locationId) => {
    if (locationId !== user.location.id && initialRolloverChanged) {
      setShowWarningForInitialRolloverLocationChanged(true)
    } else {
      setShowWarningForInitialRolloverLocationChanged(false)
    }
  }

  const onChangeStartDate = () => {
    const startDate = dayjs(form.getFieldValue('startDate') as string).format('YYYY-MM-DD')
    if (startDate !== user.startDate.split('T')[0] && initialRolloverChanged) {
      setShowWarningForInitialRolloverStartDateChanged(true)
    } else {
      setShowWarningForInitialRolloverStartDateChanged(false)
    }
  }

  const onFinish = async () => {
    try {
      const values = await form.validateFields()
      setConfirmLoading(true)
      const trimmedFirstName = (values.firstName ?? '').trim()
      const trimmedLastName = (values.lastName ?? '').trim()
      const fullName = joinFullName(trimmedFirstName, trimmedLastName)
      const newDataUser: IUserData = {
        changeTeam: false,
        changeLocation: false,
        changeStatus: false,
        changeName: false,
        changeEmail: false,
        name: fullName,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        email: user.email,
        status: user.status as UserStatus,
        teamId: user.team.id,
        locationId: user.location.id,
        isAdmin: values.role === 'Admin',
        role: values.role,
        managedLocationIds: values.role === DefaultRoleIdEnum.LOCATION_ADMIN ? values.managedLocationIds : undefined,
        startDate: dayjs(values.startDate as string).format('YYYY-MM-DD'),
        endDate: values.endDate ? dayjs(values.endDate as string).format('YYYY-MM-DD') : '',
        employeeId: values.employeeId ?? '',
        birthday: values.birthday ? dayjs(values.birthday as string).format('YYYY-MM-DD') : '',
      }

      if (values.teamId !== user.team.id) {
        newDataUser.changeTeam = true
        newDataUser.teamId = values.teamId
      }
      if (!isLocationAdmin && values.locationId !== user.location.id) {
        newDataUser.changeLocation = true
        newDataUser.locationId = values.locationId
      }
      if (!isMicrosoftBillingPayment && values.isActive !== (user.status === 'ACTIVE' ? true : false) && user.status !== 'DELETED') {
        newDataUser.changeStatus = true
        newDataUser.status = values.isActive ? 'ACTIVE' : 'INACTIVE'
      }
      if (fullName !== user.name) {
        newDataUser.changeName = true
      }
      if (values.email && values.email !== user.email) {
        newDataUser.changeEmail = true
        newDataUser.email = values.email
      }
      if (selectedFile) {
        setUploadInProgress(true)
        const imageUrl = await uploadAvatar(selectedFile)
        newDataUser.imageUrl = imageUrl
      }

      onSave(newDataUser)
      setUploadInProgress(false)
    } catch (info) {
      setConfirmLoading(false)
    }
  }

  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList
  }

  const beforePictureUpload = (file: RcFile) => {
    notification.destroy('INVALID_FILE_TYPE')
    const allowedFileFormats = ['image/png', 'image/jpeg', 'image/gif']
    if (!allowedFileFormats.includes(file.type)) {
      notification.error({
        message: formatMessage({ id: 'error.invalidFileType' }),
        key: 'INVALID_FILE_TYPE',
        description: formatMessage({ id: 'error.invalidFileType.description' }),
        duration: 0,
      })
      return
    }

    setSelectedFile(file)

    return false
  }

  const emailValidation = async (_, value) => {
    if (!value) {
      return Promise.reject(new Error(formatMessage({ id: 'form.inputRequired' })))
    }

    if (value === user.email) {
      return Promise.resolve()
    }

    if (value === authUser.email) {
      return Promise.reject(new Error(formatMessage({ id: 'components.inviteAndManageUsersWithEmail.canNotInviteYourself' })))
    }

    if (!validEmailWithRegex(value)) {
      return Promise.reject(new Error(formatMessage({ id: 'form.validEmailRequired' })))
    }

    return Promise.resolve()
  }

  return (
    <Modal
      title={<IntlMessages id="components.userUpdateForm.updateUser" />}
      open={visible}
      closeIcon={<CloseCircleOutlined />}
      confirmLoading={confirmLoading}
      okText={<IntlMessages id="app.update" />}
      onOk={() => {
        onFinish()
      }}
      okButtonProps={{
        disabled: !isEmailValid || confirmLoading,
      }}
      cancelText={<IntlMessages id="app.cancel" />}
      onCancel={() => {
        handleCancel()
      }}
    >
      <Form key="userUpdateForm" form={form} name="userUpdateForm" initialValues={defaultValue}>
        {!isMicrosoftBillingPayment && !(user.platform === 'email' && user.email?.startsWith('deleted+')) && (
          <Form.Item name="isActive" valuePropName="checked" label={<IntlMessages id="app.active" />} {...formItemLayout}>
            <Checkbox disabled={disableStatus || cannotEditAdminFields} />
          </Form.Item>
        )}
        <Form.Item
          name="role"
          label={<IntlMessages id="app.users.role" />}
          {...formItemLayout}
          rules={[{ required: true, message: <IntlMessages id="components.userUpdateForm.pleaseSelectAUserRole" /> }]}
        >
          <Select
            disabled={user.id === loggedInAdminId || companyPlan === SubscriptionPlanEnum.freemium || cannotEditAdminFields}
            placeholder={<IntlMessages id="components.userUpdateForm.pleaseSelectAUserRole" />}
            onChange={(newRole) => {
              onChangeUserRole(newRole)
            }}
          >
            <Option value="User" key="User">
              <IntlMessages id="app.user" />
            </Option>
            {(showLocationAdminOption || selectedRole === DefaultRoleIdEnum.LOCATION_ADMIN) && (
              <Option value={DefaultRoleIdEnum.LOCATION_ADMIN} key={DefaultRoleIdEnum.LOCATION_ADMIN}>
                <IntlMessages id="app.role.locationAdmin" />{' '}
                <Tag color="green">
                  <IntlMessages id="app.new" />
                </Tag>
              </Option>
            )}
            {!isLocationAdmin && (
              <Option value="Admin" key="Admin">
                <IntlMessages id="app.role.administrator" />
              </Option>
            )}
          </Select>
        </Form.Item>
        {selectedRole === DefaultRoleIdEnum.LOCATION_ADMIN && (
          <Form.Item
            name="managedLocationIds"
            label={<IntlMessages id="components.userUpdateForm.managedLocations" />}
            {...formItemLayout}
            rules={[{ required: true, message: <IntlMessages id="components.userUpdateForm.pleaseSelectManagedLocations" /> }]}
            extra={
              <span className="location-admin-info">
                <InfoCircleOutlined />
                <IntlMessages id="components.userUpdateForm.locationAdminInfo" />
              </span>
            }
          >
            <Select mode="multiple" showSearch filterOption={filterOptions} placeholder={<IntlMessages id="components.userUpdateForm.pleaseSelectManagedLocations" />}>
              {(amILocationAdmin ? locations.filter((l) => managedLocationIds.includes(l.id)) : locations)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((location) => (
                  <Option value={location.id} key={location.id}>
                    {location.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item name="firstName" label={<IntlMessages id="app.firstName" />} {...formItemLayout} rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
          <Input maxLength={100} style={{ width: 300 }} />
        </Form.Item>
        <Form.Item name="lastName" label={<IntlMessages id="app.lastName" />} {...formItemLayout}>
          <Input maxLength={100} style={{ width: 300 }} />
        </Form.Item>
        <Form.Item
          name="email"
          label={<IntlMessages id="app.email" />}
          {...formItemLayout}
          rules={[{ validator: emailValidation }]}
          validateStatus={!isEmailValid ? 'error' : undefined}
        >
          <Input
            maxLength={100}
            style={{ width: 300 }}
            allowClear
            ref={emailInputRef}
            disabled={user.platform !== 'email' || cannotEditAdminFields}
            onChange={(e) => setEmailValue(e.target.value)}
          />
        </Form.Item>

        <Form.Item
          name="teamId"
          label={<IntlMessages id="app.department" />}
          {...formItemLayout}
          rules={[{ required: true, message: <IntlMessages id="components.userUpdateForm.pleaseSelectATeam" /> }]}
        >
          <TreeSelect
            placeholder={<IntlMessages id="components.userUpdateForm.pleaseSelectATeam" />}
            disabled={teams.length === 1 ? true : false}
            showSearch
            treeNodeFilterProp="name"
            treeData={[...teams].sort((a, b) => a.name.localeCompare(b.name))}
            treeDataSimpleMode={{ id: 'id', pId: 'parentId' }}
            fieldNames={{ label: 'name', value: 'id' }}
            classNames={hasNestedTeams ? undefined : { popup: { root: 'user-update-form-flat-tree' } }}
          />
        </Form.Item>
        {!isLocationAdmin && (
          <Form.Item
            name="locationId"
            label={<IntlMessages id="app.location" />}
            {...formItemLayout}
            rules={[{ required: true, message: <IntlMessages id="components.userUpdateForm.pleaseSelectALocation" /> }]}
            extra={
              showWarningForInitialRolloverLocationChanged ? (
                <span>
                  <WarningOutlined /> <IntlMessages id="components.userUpdateForm.warningInitialRolloverForChangeLocation" />
                </span>
              ) : (
                ''
              )
            }
          >
            <Select
              showSearch
              filterOption={filterOptions}
              placeholder={<IntlMessages id="components.userUpdateForm.pleaseSelectALocation" />}
              disabled={locations.length === 1 ? true : false}
              onChange={onChangeLocation}
            >
              {locations
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((location) => (
                  <Option value={location.id} key={location.id}>
                    {location.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item
          name="startDate"
          label={<IntlMessages id="user.employeeSince" />}
          {...formItemLayout}
          rules={[{ required: true, message: <IntlMessages id="components.userUpdateForm.pleaseSelectEmployeeSince" /> }]}
          extra={
            showWarningForInitialRolloverStartDateChanged ? (
              <span>
                <WarningOutlined /> <IntlMessages id="components.userUpdateForm.warningInitialRolloverForChangeStartDate" />
              </span>
            ) : (
              ''
            )
          }
        >
          <DatePicker onChange={onChangeStartDate} />
        </Form.Item>

        <Form.Item name="birthday" label={<IntlMessages id="user.birthday" />} {...formItemLayout} rules={[{ required: false }]}>
          <DatePicker />
        </Form.Item>

        {enableUserEndDate && (
          <Form.Item name="endDate" label={<IntlMessages id="app.employeeEndDate" />} {...formItemLayout}>
            <DatePicker />
          </Form.Item>
        )}

        <Form.Item
          name="employeeId"
          label={<IntlMessages id="user.employeeId" />}
          {...formItemLayout}
          rules={[{ required: false, message: <IntlMessages id="form.inputRequired" /> }]}
        >
          <Input maxLength={32} style={{ width: 300 }} />
        </Form.Item>

        {user.id === loggedInAdminId && user.platform === 'email' && (
          <Form.Item name="profilePicture" label={<IntlMessages id="app.profilePicture" />} valuePropName="fileList" getValueFromEvent={normFile} {...formItemLayout}>
            <ImgCrop rotationSlider>
              <Upload
                listType="picture-card"
                maxCount={1}
                beforeUpload={beforePictureUpload}
                showUploadList={{
                  showPreviewIcon: false,
                  showRemoveIcon: false,
                }}
              >
                {user.imageUrl && !selectedFile ? (
                  <img src={`https://${user.imageUrl}?v=${Date.now()}`.replace('https://https://', 'https://')} alt="avatar" style={{ width: '100%' }} />
                ) : (
                  <div>
                    {uploadInProgress ? <LoadingOutlined /> : <PlusOutlined />}
                    <div style={{ marginTop: 8 }}>
                      <IntlMessages id="app.upload" />
                    </div>
                  </div>
                )}
              </Upload>
            </ImgCrop>
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
export default UserUpdateForm
